import type { ScaleOrdinal } from 'd3';
import { scaleBand, scaleLinear } from 'd3';
import clamp from 'lodash/clamp';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';

import { useResizeObserver } from '@revelio/core';

import { BAR_END_HOVER_SLOP } from '../../constants';
import type { FormatType } from '../../types';
import {
  getChartLabelFontSize,
  getFormatter,
  getMaxTextWidth,
} from '../../utilities';
import { AxisLabel } from '../axis/axis-label';
import type { BarData } from '../bar/bar-chart';
import { BarLabel } from '../bar/bar-label';
import {
  getCursorPosition,
  PlotTooltip,
  type TooltipPosition,
  useTooltipController,
} from '../plot-tooltip';
import styles from './grouped-bar-chart.module.css';
import {
  calculateGroupLayout,
  collectBarLabels,
  Dimensions,
  DimmableBar,
  type DisplayBar,
  type DisplayGroup,
  findGroupedBarHit,
  type GroupedBarHit,
  GroupedBarLegend,
  getMaxBarCount,
  getZeroedExtent,
} from './grouped-bar-shared';
import { GroupedBarTooltip } from './grouped-bar-tooltip';

type HorizontalGroupedBarProps<TData extends object> = {
  displayData: DisplayGroup<TData>[];
  colorScale: ScaleOrdinal<string, string>;
  width: number;
  height: number;
  hoveredSeries: string | null;
  setHoveredSeries: (label: string | null) => void;
  format: FormatType;
  showBarLabels: boolean;
  hideLegend: boolean;
  formatLegendLabel: (label: string) => string;
  maxLegendLabelWidth?: number;
  tooltipContent?: (group: string, bar: BarData<TData>) => JSX.Element;
};

const barHeightScale = scaleLinear().domain([1, 4]).range([22, 7]).clamp(true);
const barGroupSpacingScale = scaleLinear()
  .domain([1, 4])
  .range([36, 16])
  .clamp(true);
const barInnerSpacing = 3;

const barGroupInnerPaddingScale = scaleLinear()
  .domain([1, 4])
  .range([0.62, 0.3])
  .clamp(true);

const barInnerPaddingScale = scaleLinear()
  .domain([1, 4])
  .range([0, 0.4])
  .clamp(true);

export const HorizontalGroupedBar = <TData extends object>({
  displayData,
  colorScale,
  width,
  hoveredSeries,
  setHoveredSeries,
  format,
  showBarLabels,
  hideLegend,
  formatLegendLabel,
  maxLegendLabelWidth,
  tooltipContent,
}: HorizontalGroupedBarProps<TData>) => {
  // `height` comes from the resize observer, not the shared `height` prop —
  // that prop stays on the type because VerticalGroupedBar still honours it.
  const { containerRef, height } = useResizeObserver();
  const labelFontSize = getChartLabelFontSize();
  const barCountPerGroup = getMaxBarCount(displayData);
  const minPlotHeight =
    displayData.length * Dimensions.MinBarHeight * barCountPerGroup;

  // Single bar per group makes legend redundant with axis labels.
  const shouldHideLegend =
    hideLegend ||
    barCountPerGroup <= 1 ||
    minPlotHeight + Dimensions.LegendHeight + Dimensions.LegendTopMargin >
      height;

  const legendHeight = shouldHideLegend ? 0 : Dimensions.LegendHeight;

  const dimensionLabels = displayData.map((d) => d.group);
  const maxDimensionLabelWidth = getMaxTextWidth({
    texts: dimensionLabels,
    fontSize: labelFontSize,
  });

  const labelWidth = clamp(
    maxDimensionLabelWidth,
    Dimensions.MinLabelWidth,
    width * 0.6
  );

  const plotInnerWidth = Math.max(
    0,
    width - labelWidth - Dimensions.BarEdgePadding
  );
  const plotHeight = Math.max(
    height - legendHeight - Dimensions.HorizontalChartNewStackGap,
    minPlotHeight
  );
  const chartInsetLeft = labelWidth + Dimensions.BarEdgePadding;
  const isPlotSizeValid = plotInnerWidth > 0 && plotHeight > 0;

  const barLabels = collectBarLabels(displayData);
  const allValues = displayData
    .flatMap((d) => d.values.map((v) => v.value))
    .filter((v): v is number => v !== null);
  const formatValue = getFormatter(format);
  const totalBarLabelWidth = getMaxTextWidth({
    texts: allValues.map((value) => formatValue(value)),
    fontSize: labelFontSize,
  });
  const maxBarLabelSize =
    plotInnerWidth * Dimensions.HorizontalMaxBarLabelWidthRatio -
    Dimensions.BarLabelPadding;
  const barLabelSize = Math.min(
    Math.max(Dimensions.HorizontalMinBarLabelSize, totalBarLabelWidth),
    Math.max(0, maxBarLabelSize)
  );
  const valueSpan = Math.max(
    0,
    plotInnerWidth -
      (showBarLabels ? barLabelSize + Dimensions.BarLabelPadding : 0)
  );

  const numberOfGroups = displayData.length;
  const maxBarHeight = barHeightScale(barCountPerGroup);
  const maxGroupSpacing = barGroupSpacingScale(barCountPerGroup);
  const maxGroupHeight =
    maxBarHeight * barCountPerGroup + barInnerSpacing * (barCountPerGroup - 1);
  const maxPlotHeight =
    numberOfGroups * maxGroupHeight + (numberOfGroups - 1) * maxGroupSpacing;

  const adjustedPlotHeight = Math.min(plotHeight, maxPlotHeight);

  const groupScale = scaleBand<string>()
    .domain(displayData.map((d) => d.group))
    .range([0, adjustedPlotHeight])
    .paddingInner(barGroupInnerPaddingScale(barCountPerGroup))
    .paddingOuter(0);

  // Thin bars with a small gap between them (Figma "Dashboard 2026").
  const barScale = scaleBand()
    .domain(barLabels)
    .range([0, groupScale.bandwidth()])
    .paddingInner(barInnerPaddingScale(barCountPerGroup));

  const zeroedExtent = getZeroedExtent(displayData);
  const valueScale = zeroedExtent
    ? scaleLinear([zeroedExtent.min, zeroedExtent.max], [0, valueSpan])
    : null;

  const groupByName = new Map(displayData.map((g) => [g.group, g]));

  // Tiny non-zero bars keep 1px of paint; an actual 0 renders nothing.
  const barExtent = (value: number) => {
    if (!valueScale) return null;
    const x0 = valueScale(0);
    const x1 = valueScale(value);
    const width = value === 0 ? 0 : Math.max(1, Math.abs(x1 - x0));
    return { x: value < 0 ? x0 - width : x0, width };
  };

  const groupStarts = displayData.map((d) => groupScale(d.group));
  const [hit, setHit] = useState<GroupedBarHit | null>(null);
  const [cursor, setCursor] = useState<TooltipPosition | null>(null);
  const { isVisible, gProps } = useTooltipController({
    onHover: ([px, py], event) => {
      setCursor(getCursorPosition(event));
      let nextHit = findGroupedBarHit(
        displayData,
        groupStarts,
        groupScale.bandwidth(),
        barScale.step(),
        barScale.bandwidth(),
        () => 0,
        py
      );
      const nextBar =
        nextHit === null
          ? null
          : displayData[nextHit.groupIndex]?.values[nextHit.barIndex];
      const extent = nextBar?.value == null ? null : barExtent(nextBar.value);
      if (
        extent === null ||
        px < extent.x - BAR_END_HOVER_SLOP ||
        px > extent.x + extent.width + BAR_END_HOVER_SLOP
      ) {
        nextHit = null;
      }
      setHit(nextHit);
      setHoveredSeries(nextHit === null ? null : (nextBar?.label ?? null));
    },
  });
  useEffect(() => {
    if (!isVisible) {
      setHit(null);
      setCursor(null);
      setHoveredSeries(null);
    }
  }, [isVisible, setHoveredSeries]);

  const hitGroup = hit === null ? null : (displayData[hit.groupIndex] ?? null);
  const hitBar = hit === null ? null : (hitGroup?.values[hit.barIndex] ?? null);

  const renderBar = (
    bar: DisplayBar<TData>,
    index: number,
    groupIndex: number
  ) => {
    if (bar.value === null || !valueScale) return null;

    const extent = barExtent(bar.value);
    if (extent === null) return null;
    const { x, width: barWidth } = extent;
    const barThickness = barScale.bandwidth();
    const barY = index * barScale.step();
    const x0 = valueScale(0);
    const x1 = valueScale(bar.value);
    const labelX = Math.max(x0, x1) + Dimensions.BarLabelPadding;
    const labelSlotWidth = plotInnerWidth - labelX;
    const labelWidth = Math.min(labelSlotWidth, barLabelSize);

    return (
      <DimmableBar
        key={bar.label}
        fill={bar.color || colorScale(bar._colorKey)}
        rect={{ x, y: barY, width: barWidth, height: barThickness }}
        dimmed={hoveredSeries !== null && hoveredSeries !== bar.label}
        hovered={hit?.groupIndex === groupIndex && hit?.barIndex === index}
      >
        {showBarLabels && labelWidth > 0 && (
          <BarLabel
            x={labelX}
            y={barY}
            width={labelWidth}
            height={barThickness}
            fontSize={labelFontSize}
            label={formatValue(bar.value)}
          />
        )}
      </DimmableBar>
    );
  };

  const renderGroup = (group: DisplayGroup<TData>, groupIndex: number) => {
    const groupPos = groupScale(group.group);
    if (groupPos === undefined) return null;

    return (
      <g
        key={group.group}
        transform={`translate(0, ${groupPos})`}
        data-testid="grouped-bar-chart-group"
      >
        {group.values.map((bar, index) => renderBar(bar, index, groupIndex))}
      </g>
    );
  };

  const renderAxisLabel = (domain: string) => {
    const y = groupScale(domain);
    const group = groupByName.get(domain);
    if (y === undefined || !group) return null;

    const { groupSpan, primaryOffset } = calculateGroupLayout(
      group.values.length,
      barScale,
      groupScale.bandwidth()
    );

    return (
      <AxisLabel
        key={domain}
        y={y + primaryOffset + groupSpan / 2}
        width={labelWidth}
        fontSize={labelFontSize}
        label={domain}
        availableHeight={groupSpan}
        testId="grouped-bar-chart-axis-label"
      />
    );
  };

  return (
    <div ref={containerRef} className={styles.chartBodyHorizontal}>
      <svg width="100%" height={adjustedPlotHeight} className={styles.svg}>
        <g id="y-axis">
          {isPlotSizeValid && groupScale.domain().map(renderAxisLabel)}
        </g>
        <g id="chart" transform={`translate(${chartInsetLeft}, 0)`} {...gProps}>
          {isPlotSizeValid && (
            <rect
              className={styles.hoverOverlay}
              x={0}
              y={0}
              width={plotInnerWidth}
              height={adjustedPlotHeight}
              fill="transparent"
            />
          )}
          {isPlotSizeValid && valueScale && displayData.map(renderGroup)}
        </g>
      </svg>
      {hitGroup !== null &&
        hitBar !== null &&
        hitBar.value !== null &&
        cursor !== null && (
          <PlotTooltip
            isVisible={isVisible}
            x={cursor.x}
            y={cursor.y}
            placement="vertical"
            space="viewport"
          >
            {tooltipContent ? (
              tooltipContent(hitGroup.group, hitBar)
            ) : (
              <GroupedBarTooltip
                title={hitBar.label}
                label={hitGroup.group}
                value={getFormatter(format)(hitBar.value)}
              />
            )}
          </PlotTooltip>
        )}
      {!shouldHideLegend && isPlotSizeValid && (
        <GroupedBarLegend
          barLabels={barLabels}
          displayData={displayData}
          colorScale={colorScale}
          formatLegendLabel={formatLegendLabel}
          maxLegendLabelWidth={maxLegendLabelWidth}
          marginLeft={chartInsetLeft}
        />
      )}
    </div>
  );
};
